import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { apidata } from '../../generated/prices';
import { WebSocketClientType } from '../types';
import { WS_TRACKING_EVENTS } from '../constants/nr-events';

export type USPriceCallback = (message: apidata.prices.PriceData) => void;

interface ISubscriptions {
  path: string;
  callback: USPriceCallback;
  subId: string;
}

// Default protobuf decoder for US prices (same format as Indian prices)
const defaultProtobufDecoder = (data: Uint8Array): apidata.prices.PriceData => {
  const PriceDataClass = apidata.prices.PriceData;
  return PriceDataClass.decode(data) as unknown as apidata.prices.PriceData;
};

export interface USPricesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebSocketClientType;
  protobufDecoder?: (data: Uint8Array) => apidata.prices.PriceData;
  websocketConfig: WebSocketManagerConfig;
}

class USPricesWebSocketManager extends WebSocketManager {
  private static usPricesInstance: USPricesWebSocketManager | null = null;
  private usPricesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebSocketClientType = WebSocketClientType.US_PRICES;
  private subscriptions: Map<string, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private protobufDecoder: (data: Uint8Array) => apidata.prices.PriceData;
  private static trackEventFn:
    | ((eventName: string, data: Record<string, unknown>) => void)
    | null = null;

  private constructor(config: USPricesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.usPricesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
    // Store trackEvent from analytics config
    if (config.websocketConfig.analytics?.trackEvent) {
      USPricesWebSocketManager.trackEventFn =
        config.websocketConfig.analytics.trackEvent;
    }
  }

  private trackUSPricesEvent(
    eventName: string,
    data: Record<string, unknown>,
  ): void {
    if (USPricesWebSocketManager.trackEventFn) {
      USPricesWebSocketManager.trackEventFn(eventName, {
        ...data,
        client: this.socketId,
      });
    }
  }

  static getUSPricesInstance(
    config?: USPricesWebSocketManagerConfig,
  ): USPricesWebSocketManager {
    if (!this.usPricesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.usPricesInstance = new USPricesWebSocketManager(config);
    }
    return this.usPricesInstance;
  }

  static initializeUSPricesSocketManager(
    config: USPricesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    if (!this.usPricesInstance) {
      this.usPricesInstance = new USPricesWebSocketManager(config);
    }
    // Track initialization event
    if (USPricesWebSocketManager.trackEventFn) {
      USPricesWebSocketManager.trackEventFn(
        WS_TRACKING_EVENTS.INIT_SOCKET_MANAGER,
        {
          client: config.socketId,
          hasHashToken: !!hashToken,
          websocketUrl: config.websocketUrl,
        },
      );
    }
    super.initialize(hashToken);
    USPricesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getUSPricesWSInstance(): USPricesWebSocketManager | null {
    if (!this.usPricesInstance) {
      return null;
    }
    return this.usPricesInstance;
  }

  async initializeUSPriceConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        this.trackUSPricesEvent(
          WS_TRACKING_EVENTS.INIT_CONNECTION_ALREADY_IN_PROGRESS,
          {
            url: this.usPricesWebSocketUrl,
          },
        );
        return;
      }
      this.isConnectionInProgress = true;
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_START, {
        url: this.usPricesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.usPricesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[USPricesWebSocketManager] Error initializing US price connection:',
        error,
      );
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_ERROR, {
        url: this.usPricesWebSocketUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectUSPriceConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getUSPricesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getUSPricesConnection();

    if (!socket) {
      return;
    }

    socket.onopen = () => {
      this.subscriptions.clear();

      // Ensure socket is in the static Map (may have been cleared by cleanup/HMR)
      super.ensureSocketInMap(this.socketId, socket);

      USPricesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.CONNECTED, {
        connectionTime,
        url: this.usPricesWebSocketUrl,
      });
    };

    socket.onclose = (event) => {
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.DISCONNECTED, {
        wasClean: event.wasClean,
        code: event.code,
        reason: event.reason,
        url: this.usPricesWebSocketUrl,
      });

      USPricesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);
        const subPath = decodedMessage.subPath;
        if (!subPath) {
          console.error(
            '[USPricesWebSocketManager] No subPath found in the message',
            decodedMessage,
          );
          this.trackUSPricesEvent(WS_TRACKING_EVENTS.MESSAGE_NO_SUBPATH, {
            message: JSON.stringify(decodedMessage),
          });
          return;
        }
        const subscriptions = this.subscriptions.get(subPath);
        if (!subscriptions) {
          console.error(
            '[USPricesWebSocketManager] No subscriptions found for the subPath',
            subPath,
          );
          this.trackUSPricesEvent(WS_TRACKING_EVENTS.MESSAGE_NO_SUBSCRIPTION, {
            subPath,
          });
          return;
        }
        subscriptions.forEach((sub) => {
          try {
            sub.callback(decodedMessage);
          } catch (error: unknown) {
            console.error(
              '[USPricesWebSocketManager] Error processing WebSocket message',
              error,
            );
            this.trackUSPricesEvent(WS_TRACKING_EVENTS.MESSAGE_CALLBACK_ERROR, {
              subPath,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        });

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `[USPricesWebSocketManager] Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
          this.trackUSPricesEvent(WS_TRACKING_EVENTS.MESSAGE_PROCESSING_ERROR, {
            error: errorMessage,
          });
        }
      }
    };

    socket.onerror = (error) => {
      console.error(`WebSocket error --> ${this.socketId}`, error);
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.ERROR, {
        url: this.usPricesWebSocketUrl,
        error: String(error),
      });

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  subscribe(path: string, callback: USPriceCallback): () => void {
    try {
      const subId = `sub-${path}-us-prices-${Math.random()
        .toString(8)
        .slice(2, 11)}`;
      const socket = this.getUSPricesConnection();
      if (!socket) {
        this.trackUSPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_NO_CONNECTION, {
          path,
        });
        throw new Error('US Prices connection not initialized');
      }

      const existingSubscriptions = this.subscriptions.get(path) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState == WebSocket.OPEN) {
          const subscribeMessage = {
            action: 'SUB',
            mode: 'P',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(path, [
        ...existingSubscriptions,
        { path, callback, subId },
      ]);

      this.trackUSPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_SUCCESS, {
        path,
      });

      // Return unsubscribe function
      return () => {
        const subs = this.subscriptions.get(path);
        if (subs) {
          const filteredSubs = subs.filter((sub) => sub.subId !== subId);

          if (filteredSubs.length === 0) {
            if (socket.readyState === WebSocket.OPEN) {
              const unsubscribeMessage = {
                action: 'UNSUB',
                mode: 'P',
                entities: [`${path}`],
              };
              socket.send(JSON.stringify(unsubscribeMessage));
            }
            this.subscriptions.delete(path);
          } else {
            this.subscriptions.set(path, filteredSubs);
          }

          this.trackUSPricesEvent(WS_TRACKING_EVENTS.UNSUBSCRIBE_SUCCESS, {
            path,
          });
        }
      };
    } catch (err: unknown) {
      console.error(
        '[USPricesWebSocketManager] Error subscribing to US price',
        err,
      );
      this.trackUSPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_ERROR, {
        path,
        error: err instanceof Error ? err.message : String(err),
      });

      return () => {};
    }
  }

  isSubscribed(path: string): boolean {
    return this.subscriptions.has(path);
  }
}

export default USPricesWebSocketManager;
